import posthog from "posthog-js";
import store from "../store";
import { hasAccess } from "./tokenCheck";
import { Capacitor } from "@capacitor/core";

/**
 * Extracts email and phoneNumber from primaryCandidateInfo in Vuex store (candidateVUEX).
 */
export function getUserContactInfo() {
	let email = "";
	let phoneNumber = "";
	try {
		const storeInstance = store && store.state ? store : null;
		if (storeInstance) {
			const primaryCandidateInfo =
				storeInstance.state?.candidateVUEX?.primaryCandidateInfo;
			email = primaryCandidateInfo?.email || "";
			phoneNumber = primaryCandidateInfo?.phoneNumber || "";
		}
	} catch (err) {
		console.warn("[PostHog] Failed to extract contact info from store:", err);
	}
	return { email, phoneNumber };
}

export const isApp = () => {
	try {
		return Capacitor.isNativePlatform();
	} catch (e) {
		return false;
	}
};

/**
 * Returns the event name formatted according to the current platform.
 * When Capacitor.isNativePlatform() is true, event names are prefixed with 'app_'.
 *
 * @param {string} eventName
 * @returns {string}
 */
export function getEventName(eventName) {
	if (!eventName) return eventName;
	if (isApp()) {
		return eventName.startsWith("app_") ? eventName : `app_${eventName}`;
	}
	return eventName;
}

// signup and login events

export function trackSignupStarted(identifier, useEmail = true) {
	const key = useEmail ? "email" : "phoneNumber";
	posthog.capture(getEventName("signup_started"), {
		[key]: identifier,
	});
}

export function trackSignupCompleted(identifier, useEmail = true) {
	const key = useEmail ? "email" : "phoneNumber";
	posthog.capture(getEventName("signup_completed"), {
		[key]: identifier,
	});
}

export function trackLogin(identifier, useEmail = true) {
	const key = useEmail ? "email" : "phoneNumber";
	posthog.capture(getEventName("login"), {
		[key]: identifier,
	});
}

export function trackLoggedIn(identifier, useEmail = true) {
	const key = useEmail ? "email" : "phoneNumber";
	posthog.identify(identifier, {
		[key]: identifier,
	});
}

// other events in the project

const SESSION_STORAGE_KEY = "hire3x_ph_tracked_events";
const inMemoryTrackedEvents = new Set();

/**
 * Retrieves the set of events already captured in the current session.
 */
function getTrackedEvents() {
	try {
		if (typeof window !== "undefined" && window.sessionStorage) {
			const data = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
			if (data) {
				return new Set(JSON.parse(data));
			}
		}
	} catch (err) {
		console.warn("[PostHog] Failed to read from sessionStorage:", err);
	}
	return inMemoryTrackedEvents;
}

/**
 * Checks if a specific event key has already fired in this session.
 */
function hasEventBeenTracked(eventKey) {
	const tracked = getTrackedEvents();
	return tracked.has(eventKey);
}

/**
 * Marks a specific event key as tracked in the current session.
 */
function markEventAsTracked(eventKey) {
	inMemoryTrackedEvents.add(eventKey);
	try {
		if (typeof window !== "undefined" && window.sessionStorage) {
			const tracked = getTrackedEvents();
			tracked.add(eventKey);
			window.sessionStorage.setItem(
				SESSION_STORAGE_KEY,
				JSON.stringify(Array.from(tracked))
			);
		}
	} catch (err) {
		console.warn("[PostHog] Failed to save to sessionStorage:", err);
	}
}

/**
 * Clears the session tracking cache (e.g. upon logout).
 */
export function clearSessionTrackedEvents() {
	inMemoryTrackedEvents.clear();
	try {
		if (typeof window !== "undefined" && window.sessionStorage) {
			window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
		}
	} catch (err) {
		console.warn("[PostHog] Failed to clear sessionStorage:", err);
	}
}

/**
 * Captures an event via PostHog at most once per user session.
 * @param {string} eventName - PostHog event name
 * @param {Object} properties - Event properties payload
 * @param {string|number|null} distinctKey - Entity/context identifier (e.g. jobID, quizId)
 */
export function captureOnce(eventName, properties = {}, distinctKey = null) {
	if (!hasAccess()) return;
	if (!eventName) return;

	const resolvedEventName = getEventName(eventName);

	const contactInfo = getUserContactInfo();
	const finalProperties = {
		...properties,
		email: contactInfo.email || "",
		phoneNumber: contactInfo.phoneNumber || "",
	};

	const trackingKey =
		distinctKey !== null && distinctKey !== undefined && distinctKey !== ""
			? `${resolvedEventName}:${distinctKey}`
			: resolvedEventName;

	if (hasEventBeenTracked(trackingKey)) {
		return;
	}

	markEventAsTracked(trackingKey);

	try {
		posthog.capture(resolvedEventName, finalProperties);
	} catch (err) {
		console.warn(`[PostHog] Failed to capture event "${resolvedEventName}":`, err);
	}
}

export function trackJobViewed(job) {
	if (!job) return;
	captureOnce(
		"job_viewed",
		{
			job_id: job.jobID,
			job_title: job.title,
			customerID: job.customerID,
			customerName: job.customerName,
		},
		job.jobID || job.id
	);
}

export function trackJobApplied(job) {
	if (!job) return;
	captureOnce(
		"job_applied",
		{
			job_id: job.jobID,
			job_title: job.title,
			customerID: job.customerID,
			customerName: job.customerName,
		},
		job.jobID || job.id
	);
}

export function trackResumeDownloaded(resumeType) {
	captureOnce(
		"resume_downloaded",
		{
			resumeType: resumeType,
		},
		resumeType
	);
}

export function trackResumeUploaded(profileData) {
	const data = profileData || {};
	const distinctKey = data.candidateID || data.id || data.email;
	captureOnce(
		"resume_uploaded",
		{
			...data,
		},
		distinctKey
	);
}

export function trackResumeCreated(profileData) {
	const data = profileData || {};
	const distinctKey = data.candidateID || data.id || data.email;
	captureOnce(
		"resume_created",
		{
			...data,
		},
		distinctKey
	);
}

export function trackHomePageLoaded(profileData) {
	captureOnce("homepage_loaded", {
		...(profileData || {}),
	});
}

export function trackAutoFilledFromResume(profileData) {
	const data = profileData || {};
	const distinctKey = data.candidateID || data.id || data.email;
	captureOnce(
		"auto_filled_from_resume",
		{
			...data,
		},
		distinctKey
	);
}

export function trackFormSubmitted(formDetails) {
	const data = formDetails || {};
	const distinctKey = data.formID || data.formType || data.id;
	captureOnce(
		"form_submitted",
		{
			...data,
		},
		distinctKey
	);
}

export function trackSelfAssessPlayed(AssessDetails) {
	const data = AssessDetails || {};
	const distinctKey = data.testID || data.id || data.testName;
	captureOnce(
		"self_asess_played",
		{
			...data,
		},
		distinctKey
	);
}

export function trackAssessMarksViewed(assessDetails) {
	const data = assessDetails || {};
	const distinctKey =
		data.testID || data.groupID
			? `${data.testID || ""}_${data.groupID || ""}`
			: data.id;
	captureOnce(
		"result_viewed",
		{
			...data,
		},
		distinctKey
	);
}

export function trackGameStarted(gameDetails) {
	const data = gameDetails || {};
	const distinctKey = data.gameType || data.gameId || data.id;
	captureOnce(
		"game_started",
		{
			...data,
		},
		distinctKey
	);
}

export function trackGameEnded(gameDetails) {
	const data = gameDetails || {};
	const distinctKey = data.gameType || data.gameId || data.id;
	captureOnce(
		"game_completed",
		{
			...data,
		},
		distinctKey
	);
}

export function trackTimeSpent(pageDetails) {
	const data = pageDetails || {};
	const distinctKey = data.page || data.pageName;
	captureOnce(
		"page_time_spent",
		{
			...data,
		},
		distinctKey
	);
}

export function trackAccountDeleted(data) {
	const payload = data || {};
	const distinctKey = payload.email || payload.userID || payload.id;
	captureOnce(
		"account_deleted",
		{
			...payload,
		},
		distinctKey
	);
}

export function trackInstructorApplied(data) {
	const payload = data || {};
	const distinctKey = payload.email || payload.instructorId || payload.id;
	captureOnce(
		"instructor_applied",
		{
			...payload,
		},
		distinctKey
	);
}

export function trackPanelApplied(data) {
	const payload = data || {};
	const distinctKey = payload.email || payload.panelistId || payload.id;
	captureOnce(
		"panelist_applied",
		{
			...payload,
		},
		distinctKey
	);
}

export function trackQuizSubmitted(data) {
	const payload = data || {};
	const distinctKey =
		payload.quizId || payload.quizID || payload.id || payload.date;
	captureOnce(
		"quiz_submitted",
		{
			...payload,
		},
		distinctKey
	);
}

export function trackSurveyTaken(data) {
	const payload = data || {};
	const distinctKey = payload.surveyId || payload.surveyID || payload.id;
	captureOnce(
		"survey_taken",
		{
			...payload,
		},
		distinctKey
	);
}

export function trackResumeClinicUsed(data) {
	const payload = data || {};
	const distinctKey = payload.reportId || payload.email || payload.id;
	captureOnce(
		"resumeclinic_report_used",
		{
			...payload,
		},
		distinctKey
	);
}

export function trackResumeClinicReportDownloaded(data) {
	const payload = data || {};
	const distinctKey = payload.reportId || payload.email || payload.id;
	captureOnce(
		"resumeclinic_report_downloaded",
		{
			...payload,
		},
		distinctKey
	);
}

export function trackResumeClinicReportViewed(data) {
	const payload = data || {};
	const distinctKey = payload.reportId || payload.email || payload.id;
	captureOnce(
		"resumeclinic_report_viewed",
		{
			...payload,
		},
		distinctKey
	);
}

export function trackDashboardVisited(data = {}) {
	captureOnce("dashboard_visited", data);
}

export function trackResumeBuilderVisited(data = {}) {
	captureOnce("resume_builder_visited", data);
}

export function trackJobsVisited(data = {}) {
	captureOnce("jobs_visited", data);
}

export function trackAssessmentsVisited(data = {}) {
	captureOnce("assessments_visited", data);
}

export function trackAssessmentViewed(data = {}) {
	captureOnce("assessment_viewed", data, data.id);
}

export function trackFixResumeVisited(data = {}) {
	captureOnce("fix_resume_visited", data);
}

export function trackMockInterviewVisited(data = {}) {
	captureOnce("mock_interview_visited", data);
}

export function trackPrepareBySkill(data = {}) {
	captureOnce("prepare_by_skill", data);
}

export function trackPrepareByCompany(data = {}) {
	captureOnce("prepare_by_company", data);
}

export function trackMockIntStarted(data = {}) {
	captureOnce("mock_int_started", data);
}

export function trackMockIntCompleted(data = {}) {
	captureOnce("mock_int_completed", data);
}

export function trackSignupDetailsFilled(data = {}) {
	captureOnce("signup_from_filled", data);
}

export function trackOnboardingStarted(data = {}) {
	captureOnce("onboarding_started", data);
}

export function trackOnboardingSkipped(data = {}) {
	captureOnce("onboarding_skipped", data);
}

export function trackOnboardingCompleted(data = {}) {
	captureOnce("onboarding_completed", data);
}

export default trackAccountDeleted;
